import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add JWT token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('selectedProjectId');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (username, password) => api.post('/auth/login', { username, password }),
  getMe: () => api.get('/auth/me'),
};

// User API
export const userAPI = {
  getMyProjects: () => api.get('/user/projects'),
};

// Admin API
export const adminAPI = {
  // Users
  getUsers: () => api.get('/admin/users'),
  getUser: (id) => api.get(`/admin/users/${id}`),
  createUser: (data) => api.post('/admin/users', data),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  assignProjects: (userId, projectIds) => api.post(`/admin/users/${userId}/projects`, { projectIds }),

  // Projects
  getProjects: () => api.get('/admin/projects'),
  getProject: (id) => api.get(`/admin/projects/${id}`),
  createProject: (data) => api.post('/admin/projects', data),
  updateProject: (id, data) => api.put(`/admin/projects/${id}`, data),
  deleteProject: (id) => api.delete(`/admin/projects/${id}`),

  // Areas
  getProjectAreas: (projectId) => api.get(`/admin/projects/${projectId}/areas`),
  getArea: (id) => api.get(`/admin/areas/${id}`),
  createArea: (projectId, data) => api.post(`/admin/projects/${projectId}/areas`, data),
  updateArea: (id, data) => api.put(`/admin/areas/${id}`, data),
  deleteArea: (id) => api.delete(`/admin/areas/${id}`),

  // Config
  getConfig: () => api.get('/admin/config'),
  updateConfig: (data) => api.put('/admin/config', data),

  // Costs
  getCosts: () => api.get('/admin/costs'),

  // Documents
  getAreaDocuments: (areaId) => api.get(`/admin/areas/${areaId}/documents`),
  getDocument: (id) => api.get(`/admin/documents/${id}`),
  getDocumentContent: (id) => api.get(`/admin/documents/${id}/content`),
  updateDocumentContent: (id, content) => api.put(`/admin/documents/${id}/content`, { content }),
  deleteDocument: (id) => api.delete(`/admin/documents/${id}`),
  uploadDocument: (areaId, file) => {
    const formData = new FormData();
    formData.append('document', file);
    return api.post(`/admin/areas/${areaId}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Chat API
export const chatAPI = {
  sendMessage: (message, projectId, conversationHistory = []) =>
    api.post('/chat', { message, projectId, conversationHistory }),
  getKnowledgeStatus: (projectId) => api.get('/knowledge/status', { params: { projectId } }),
};

export default api;
